/* eslint-disable react/react-in-jsx-scope */
import IconExcel from "@/assets/icones/excel.svg?react";
import IconOds from "@/assets/icones/ods.svg?react";
import IconPdf from "@/assets/icones/pdf.svg?react";
import IconWord from "@/assets/icones/word.svg?react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Button, buttonVariants } from "@/components/ui/button";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import { TreeRoot } from "@/types";
import { format } from "date-fns";
import { Download, FolderOpen, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type DocumentItem = {
  id: number;
  name: string;
  path: string;
  pp: number;
  program: number;
  project: number;
  type: number;
  at?: {
    date?: string;
  };
  createdAt?: string;
};

type KnownFileType = "pdf" | "doc" | "docx" | "xlsx" | "ods";

const getDocumentSubtype = (doc: Record<string, unknown>): number | null => {
  const candidate = doc.subType ?? doc.idSubcategory ?? doc.type;
  const numeric = Number(candidate);
  return Number.isNaN(numeric) ? null : numeric;
};

const DocumentList = ({ data, typesDocs }: { data: any; typesDocs: any }) => {
  const [openItems, setOpenItems] = useState<string[]>([]);
  const [groupBy, setGroupBy] = useState("chronologie");
  const { data: treeFinancer, loading: loadingTreeFinancer } = useFetchDataV2<
    TreeRoot[]
  >(
    `${import.meta.env.VITE_API_ENDPOINT}/tree/financer`,
    `tree-financer`,
    1000 * 60 * 60
  );
  if (loadingTreeFinancer) {
    return <LoadingSpinner />;
  }

  const subTypeToCategoryMap = useMemo(() => {
    const map = new Map<number, { category: string; subcategory: string }>();
    typesDocs?.forEach((parent: any) => {
      parent.children?.forEach((child: any) => {
        map.set(Number(child.value), {
          category: parent.label,
          subcategory: child.label,
        });
      });
    });
    return map;
  }, [typesDocs]);

  const groupedDocuments = useMemo(() => {
    const groups = new Map<
      string,
      { key: string; label: string; date: Date; docs: DocumentItem[] }
    >();

    (data as DocumentItem[])?.forEach((doc) => {
      const categoryInfo = subTypeToCategoryMap.get(
        getDocumentSubtype(doc as unknown as Record<string, unknown>) ?? -1,
      );

      if (groupBy === "categorie") {
        const categoryKey = categoryInfo?.category || "Autres";
        const existing = groups.get(categoryKey);
        if (existing) {
          existing.docs.push(doc);
        } else {
          groups.set(categoryKey, {
            key: categoryKey,
            label: categoryKey,
            date: new Date(0),
            docs: [doc],
          });
        }
        return;
      }

      const rawDate = doc.at?.date || doc.createdAt;
      const parsed = rawDate ? new Date(rawDate) : null;

      if (!parsed || Number.isNaN(parsed.getTime())) {
        const undated = groups.get("non-date") || {
          key: "non-date",
          label: "non daté",
          date: new Date(0),
          docs: [],
        };
        undated.docs.push(doc);
        groups.set("non-date", undated);
        return;
      }

      const key = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, "0")}`;
      const existing = groups.get(key);
      if (existing) {
        existing.docs.push(doc);
      } else {
        groups.set(key, {
          key,
          label: parsed.toLocaleDateString("fr-FR", {
            month: "long",
            year: "numeric",
          }),
          date: new Date(parsed.getFullYear(), parsed.getMonth(), 1),
          docs: [doc],
        });
      }
    });

    return [...groups.values()].sort((a, b) =>
      groupBy === "categorie"
        ? a.label.localeCompare(b.label, "fr")
        : b.date.getTime() - a.date.getTime(),
    );
  }, [data, groupBy, subTypeToCategoryMap]);

  useEffect(() => {
    setOpenItems(groupedDocuments.map((group) => group.key));
  }, [groupedDocuments]);

  return (
    <>
      <div className="mb-4 flex justify-end">
        <Select value={groupBy} onValueChange={setGroupBy}>
          <SelectTrigger className="w-40 border-0 px-4 font-normal text-foreground/90">
            <SelectValue placeholder="Trier par" />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              <SelectItem value="categorie">Sous-catégorie</SelectItem>
              <SelectItem value="chronologie">Chronologie</SelectItem>
            </SelectGroup>
          </SelectContent>
        </Select>
      </div>
      <Accordion
        type="multiple"
        className="w-full"
        value={openItems}
        onValueChange={setOpenItems}
      >
        {groupedDocuments.map((group) => (
          <AccordionItem value={group.key} className="border-0 mb-4" key={group.key}>
            <AccordionTrigger className="py-[10px] hover:no-underline group">
              <div className="flex justify-between items-start w-full bg-[#FFFF] px-[18px] py-[10px] rounded-[9px]">
                <div className="flex items-center gap-2 flex-1 text-left pr-4">
                  <FolderOpen className="text-gray-950" />
                  <div className="flex-1 group-hover:underline text-gray-900">
                    {group.label}
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="bg-white -mt-4 pt-8 px-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Porteurs de projet</TableHead>
                    <TableHead>Programmes</TableHead>
                    <TableHead>Projets</TableHead>
                    <TableHead>Date d'ajout</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {group.docs.map((pj, key) => {
                    const extension = pj.name.split(".").pop()?.toLowerCase() || "pdf";
                    const iconType: KnownFileType = (
                      ["pdf", "doc", "docx", "xlsx", "ods"].includes(extension)
                        ? extension
                        : "pdf"
                    ) as KnownFileType;
                    const safePath = pj.path
                      .split("/")
                      .map((segment) => encodeURIComponent(segment))
                      .join("/");
                    return (
                      <TableRow
                        key={pj.id ?? key}
                        className={`${key % 2 == 0 ? "bg-gray-50" : "bg-white"}`}
                      >
                        <TableCell className="font-medium flex items-center gap-x-2 align-top w-80">
                          <TypeFile type={iconType} />
                          <span className="truncate">{pj.name}</span>
                        </TableCell>
                        <TableCell className="align-top w-40">
                          {Number(pj.type) > 99
                            ? typesDocs.find((t: any) => Number(t.value) === Number(pj.type))
                                ?.label ?? "Inconnu"
                            : (() => {
                                const foundChild = typesDocs
                                  .map((t: any) =>
                                    t.children.find(
                                      (c: any) => Number(c.value) === Number(pj.type)
                                    )
                                  )
                                  .find((c: any) => c !== undefined);
                                return foundChild?.label ?? "Inconnu";
                              })()}
                        </TableCell>

                        <TableCell className="align-top">
                          {treeFinancer?.find((t) => t.id === pj.pp)?.name}
                        </TableCell>
                        <TableCell className="align-top">
                          {
                            treeFinancer
                              ?.find((t) => t.id === pj.pp)
                              ?.programs.find((p) => p.id === pj.program)?.name
                          }
                        </TableCell>
                        <TableCell className="align-top">
                          {
                            treeFinancer
                              ?.find((t) => t.id === pj.pp)
                              ?.programs.find((p) => p.id === pj.program)
                              ?.projects.find((p) => p.id === pj.project)?.name
                          }
                        </TableCell>
                        <TableCell className="align-top">
                          {pj.at && format(new Date(pj.at.date as string), "dd/MM/yyyy")}
                        </TableCell>
                        <TableCell className="align-top">
                          <div className="flex items-center gap-2">
                            <a
                              href={`${import.meta.env.VITE_IP_API}/media/${safePath}`}
                              download
                              className={buttonVariants({
                                variant: "ghost",
                                className:
                                  "flex gap-2 items-center cursor-pointer text-foreground",
                              })}
                            >
                              <Download width={22} height={22} />
                            </a>
                            <Button
                              variant={"ghost"}
                              size={"icon"}
                              className="hover:bg-red-500 hover:text-white text-red-500"
                            >
                              <Trash2 width={22} height={22} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </>
  );
};
const TypeFile = ({
  type,
}: {
  type: KnownFileType;
}) => {
  const icon = {
    pdf: <IconPdf width={22} height={22} />,
    doc: <IconWord width={22} height={22} />,
    docx: <IconWord width={22} height={22} />,
    xlsx: <IconExcel width={22} height={22} />,
    ods: <IconOds width={22} height={22} />,
  };
  return <div>{icon[type as keyof typeof icon] ?? <IconPdf width={22} height={22} />}</div>;
};
export default DocumentList;
